/**
 * Words for a dataset source (docs/DATASETS.md): what rows come from, what
 * its filter says, and how it is doing. Pure, so the strip, the dialog and
 * the tests read one thing.
 */
import { conditionText, filterToConditions } from 'features/datasets/components/EvalConditions';

export const KIND_LABEL = { spans: 'Spans', runs: 'Runs', conversations: 'Conversations', verdicts: 'Verdicts', eval_results: 'Eval results' };

/**
 * A stored source back into the Add source builder's state, so Update can
 * start from the filters the dataset was made with: the agent / trace chips
 * become the pickers, every other chip a filter, eval conditions lines.
 */
export function stateFromSource(source) {
  const f = source?.filter || {};
  const chips = Array.isArray(f.modularFilters) ? f.modularFilters : [];
  const picker = chips.find((c) => c && (c.category === 'agents' || c.category === 'traces') && Array.isArray(c.value));
  let n = 0;
  const rest = chips.filter((c) => c && c !== picker).map((c) => ({ ...c, id: `f${Date.now().toString(36)}${n++}` }));
  const agents = picker && picker.category === 'agents' ? picker.value.map((a) => ({ agentName: a, name: a, agent_name: a })) : [];
  const traces = picker && picker.category === 'traces' ? picker.value.map((t) => ({ traceName: t })) : [];
  return {
    kind: kindOf(source),
    selectedAgents: agents, agentScopedFilters: [], selectedTraces: traces, traceScopedFilters: [], globalFilters: rest,
    conditions: filterToConditions(f.eval_conditions), anyKind: f.span_kind === 'any',
    lineageIds: Array.isArray(f.lineage_ids) ? f.lineage_ids : [], outcomes: Array.isArray(f.outcomes) ? f.outcomes : ['failed'],
    includeTrace: source?.options?.include_trace === true,
    nodeId: source?.options?.node_id || '',
  };
}

/** The kind a source shows: verdicts when it writes verdict rows. */
export function kindOf(source) {
  if (source?.kind_shown) return source.kind_shown;
  return source?.kind === 'eval_results' && source?.options?.rows === 'verdicts' ? 'verdicts' : source?.kind || 'spans';
}

/** Periods a source keeps as a preset; each run measures them from its own start. Anything else is Custom. */
export const RELATIVE_PRESETS = ['today', 'yesterday', '24h', '7d', '30d', '3m', '6m', '12m'];
export const isRelativePreset = (p) => RELATIVE_PRESETS.includes(String(p || ''));
const PRESET_LABEL = { today: 'today', yesterday: 'yesterday', '24h': 'last 24h', '7d': 'last 7d', '30d': 'last 30d', '3m': 'last 3m', '6m': 'last 6m', '12m': 'last 12m' };

const timeText = (t) => {
  if (!t) return 'last 7d';
  if (isRelativePreset(t.preset)) return PRESET_LABEL[t.preset];
  if (t.from && t.to) return `${t.from.slice(0, 10)} – ${t.to.slice(0, 10)}`;
  if (t.preset && t.preset !== 'custom') return `last ${t.preset}`;
  return 'last 7d';
};

/**
 * The words for a finished run of a source: { text, tone: 'ok' | 'empty' | 'failed' }.
 * `feed` is the run (dataset_feeds row: status, rows_matched, rows_written, rows_skipped, error).
 * Custom names its dates; a relative period names the period.
 */
export function runOutcome(source, feed) {
  if (!feed) return { text: '', tone: 'ok' };
  const t = source?.filter?.time;
  const relative = isRelativePreset(t?.preset);
  const range = t?.from || t?.to ? `${t.from ? t.from.slice(0, 10) : '…'} to ${t.to ? t.to.slice(0, 10) : 'now'}` : 'the last 7 days';
  const when = relative ? (t.preset === 'today' || t.preset === 'yesterday' ? t.preset : `in the ${timeText(t)}`) : `from ${range}`;
  if (feed.status === 'failed') return { text: `Sync failed${feed.error ? `: ${feed.error}` : '.'}`, tone: 'failed' };
  if (feed.status === 'refused') return { text: 'Sync stopped: out of executions, so nothing was added.', tone: 'failed' };
  const written = Number(feed.rows_written) || 0;
  const matched = Number(feed.rows_matched) || 0;
  if (written > 0) return { text: `Synced: ${written.toLocaleString()} new row${written === 1 ? '' : 's'} added.`, tone: 'ok' };
  if (matched === 0) return { text: `No records matched ${when}. No new data was added.`, tone: 'empty' };
  return { text: `No new data added: the ${matched.toLocaleString()} matching record${matched === 1 ? '' : 's'} ${when} ${matched === 1 ? 'is' : 'are'} already in this dataset.`, tone: 'empty' };
}

/** "last 7d · agents: a, b · 2 filters · Grounding failed", with eval names when known. */
export function filterSummary(source, lineageNames = {}, { withTime = true } = {}) {
  const f = source?.filter || {};
  const parts = withTime ? [timeText(f.time)] : [];
  const chips = Array.isArray(f.modularFilters) ? f.modularFilters : [];
  const agents = chips.find((c) => c && c.field === 'agent_name' && Array.isArray(c.value));
  if (agents) parts.push(`agents: ${agents.value.join(', ')}`);
  const rest = chips.filter((c) => c !== agents).length;
  if (rest) parts.push(`${rest} filter${rest === 1 ? '' : 's'}`);
  if (Array.isArray(f.lineage_ids) && f.lineage_ids.length) parts.push(`evals: ${f.lineage_ids.map((id) => lineageNames[id] || 'eval').join(', ')}`);
  if (Array.isArray(f.outcomes) && f.outcomes.length) parts.push(f.outcomes.map((o) => o.replace('_', ' ')).join(' or '));
  if (Array.isArray(f.evaluated_by) && f.evaluated_by.length) parts.push(`evaluated by ${f.evaluated_by.map((id) => lineageNames[id] || 'eval').join(', ')}`);
  if (f.verdict) parts.push(f.verdict === 'fail' ? 'failed' : 'passed');
  if (f.thumbs) parts.push(`marked ${f.thumbs === 'down' ? 'wrong' : 'right'}`);
  for (const c of Array.isArray(f.eval_conditions) ? f.eval_conditions : []) parts.push(conditionText(c, lineageNames));
  if (f.span_kind === 'any') parts.push('any span kind');
  return parts.join(' · ');
}

/**
 * How a source is doing: { text, tone }.
 *   tone: live | paused | running | once | refused | failed | agent
 */
export function statusOf(source) {
  if (!source) return { text: '', tone: 'once' };
  if (source.agent) return { text: source.agent.status === 'active' ? 'Agent · live' : 'Agent · paused', tone: source.agent.status === 'active' ? 'live' : 'paused' };
  const s = source.last_status;
  if (s === 'queued') return { text: 'Queued', tone: 'running' };
  if (s === 'running') return { text: 'Running…', tone: 'running' };
  if (s === 'refused') return { text: 'Out of executions', tone: 'refused' };
  if (s === 'failed') return { text: 'Failed', tone: 'failed' };
  if (source.live && !source.paused_at) return { text: 'Live', tone: 'live' };
  if (source.live && source.paused_at) return { text: 'Paused', tone: 'paused' };
  return { text: 'Once', tone: 'once' };
}

export function rowsText(source) {
  const n = Number(source?.rows_total) || 0;
  return `${n.toLocaleString()} row${n === 1 ? '' : 's'}`;
}
